import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import invoiceApi from "../../api/invoice";
import paymentApi from "../../api/payment";
import { useDirectorList } from "../../api/userCurrent";
import { useDeveloperBankAccountList } from "../../api/utilities";
import { currentUserSelector } from "../../redux/slices/auth/selectors";
import { Loader, toast } from "../../common/components/core";
import { FormFooter } from "../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { BesuClaimState } from "../../types/claim";
import { BankAccount, Director } from "../../types/common";
import { InvoiceState, InvoiceStatus } from "../../types/invoice";
import { BesuProjectState } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../utils/errorHandling";
import ClaimContext from "../claim/ClaimContext";
import InvoiceContext from "../invoice/InvoiceContext";
import ProjectContext from "../project/ProjectContext";
import PaymentBeneficiaryDetails from "./components/PaymentBeneficiaryDetails";
import PaymentForm, { PaymentFormValues } from "./components/PaymentForm";
import PaymentOrderPreview from "./components/PaymentOrderPreview";

const NewPaymentView = (): JSX.Element => {
    document.title = PageTitle.NewPaymentView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const {
        data: claim
    } = useContext(ClaimContext);
    const {
        data: {
            state: { data: invoice },
            ref: invoiceRef,
        },
        mutate: mutateInvoice,
    } = useContext(InvoiceContext);
    const currentUser = useSelector(currentUserSelector);
    const { data: bankAccounts, isValidating: isBankAccountsLoading } =
        useDeveloperBankAccountList(currentUser?.userProfile.id);
    const { data: directors, isValidating: isDirectorsLoading } =
        useDirectorList();
    const directorList: Director[] = directors
        ? [...directors].sort((a, b) => a.fullName?.localeCompare(b.fullName))
        : [];
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);

    const submit = async (paymentFormValues: PaymentFormValues) => {
        setSubmitting(true);
        try {
            let targetInvoiceState = invoice;
            let targetInvoiceRef = invoiceRef;
            if (invoice.status !== InvoiceStatus.FINANCE_APPROVED) {
                const invoiceApprovalResult = await invoiceApi.financeApprove(
                    invoiceRef,
                );
                targetInvoiceState =
                    invoiceApprovalResult.stateAndRefMap.InvoiceState[0].state
                        .data;
//                 targetInvoiceRef =
//                     invoiceApprovalResult.stateAndRefMap.InvoiceState[0].ref;
            }
            const paymentCreationResult = await paymentApi.create(
                targetInvoiceRef,
                paymentFormValues,
                targetInvoiceState,
                bankAccounts ?? [],
                project.name,
            );
            mutateInvoice();
            const customerRefNumber =
                paymentCreationResult.stateAndRefMap.PaymentOrderState[0]?.state
                    .data.props.customerRefNumber;
            if (customerRefNumber) {
                toast(`Payment order #${customerRefNumber} created`);
            }

            history.push(
                `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
            );
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers(
                onStaleStateError(() => {
                    mutateInvoice();
                    history.push(
                        `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
                    );
                    toast("Page has been updated by another user.");
                }),
                onAuthError,
            )(e as GenericError);
        }
    };

    return (
        <PureNewPaymentView
            project={project}
            claim={claim}
            invoice={invoice}
            payerList={bankAccounts ?? []}
            directorList={directorList ?? []}
            isLoading={
                isSubmitting || isBankAccountsLoading || isDirectorsLoading
            }
            onSubmit={submit}
        />
    );
};

type PureNewPaymentViewProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    invoice: InvoiceState;
    payerList: BankAccount[];
    directorList: Director[];
    isLoading: boolean;
    onSubmit: (paymentFormValues: PaymentFormValues) => void;
};

enum NewPaymentStep {
    PaymentInput,
    Preview,
}

export const PureNewPaymentView = ({
    project,
    claim,
    invoice,
    payerList,
    isLoading,
    onSubmit,
}: PureNewPaymentViewProps): JSX.Element => {
    const [step, setStep] = useState(NewPaymentStep.PaymentInput);
    const [paymentFormValues, setPaymentFormValues] =
        useState<PaymentFormValues>();

    useEffect(() => {
        if (!isLoading) {
            setStep(NewPaymentStep.PaymentInput);
        }
    }, [isLoading]);

    const moveToPreviewPage = (values: PaymentFormValues) => {
        setPaymentFormValues(values);
        setStep(NewPaymentStep.Preview);
    };

    return (
        <Loader overlay isLoading={isLoading} bgClassName="bg-body">
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink
                        to={`/project/${project.referenceId}/claim/${claim.referenceId}`}
                    >
                        Claim {shortenUUID(claim.referenceId)}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink
                        to={`/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`}
                    >
                        Invoice {shortenUUID(invoice.linearId.id)}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>New Payment</Breadcrumb.Item>
            </Breadcrumb>
            {paymentFormValues && step === NewPaymentStep.Preview && (
                <>
                    <h1 className="mb-3">Preview</h1>
                    <PaymentOrderPreview
                        paymentFormValues={paymentFormValues}
                        invoice={invoice}
                        payerList={payerList}
                        paymentOrderApprovers={
                            project.paymentOrderApprovers
                        }
                    />
                    <FormFooter className="d-flex justify-content-between">
                        <Button
                            variant="light.tertiary"
                            onClick={() => setStep(NewPaymentStep.PaymentInput)}
                        >
                            Back
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={() => onSubmit(paymentFormValues)}
                        >
                            Submit
                        </Button>
                    </FormFooter>
                </>
            )}
            {step === NewPaymentStep.PaymentInput && (
                <>
                    <PaymentBeneficiaryDetails invoice={invoice} />
                    <hr className="bg-border-grey my-3" />
                    <PaymentForm
                        projectName={project.name}
                        invoice={invoice}
                        payerList={payerList}
                        values={paymentFormValues}
                        onSubmit={moveToPreviewPage}
                    />
                </>

            )}
        </Loader>
    );
};

export default NewPaymentView;
